// =============================================================================
// Auditoria de Saques — Portal Arquitetos (admin)
// =============================================================================
// Visualização SOMENTE LEITURA do histórico de solicitações de saque:
//   - Quem pediu, quando, valor, status atual (PENDENTE/PAGO/RECUSADO/CANCELADO)
//   - Quem aprovou (ou recusou) no portal-financeiro, quando, com qual resposta
//   - Todos os e-mails disparados (pra quem, sucesso/falha, message-id)
//   - Itens (CREINDs) que compunham a solicitação + situação atual no ERP
//
// NÃO permite pagar/aprovar/recusar — a operação fica exclusivamente no
// portal-financeiro. Permissão: `auditoria_saques`.
// =============================================================================

import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import EmptyState from '../components/EmptyState';
import { useModalSafeClose } from '../helpers/useModalSafeClose';

const VERDE = '#1F6A2D';
const VERDE_ESCURO = '#1F6A2D';
const LARANJA = '#E2901A';
const VERMELHO = '#dc3545';
const AZUL = '#1d6fb8';
const CINZA = '#6F6A63';

const brl = v => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

function fmtData(iso, comHora = true) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const opts = comHora
    ? { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }
    : { day: '2-digit', month: '2-digit', year: 'numeric' };
  return d.toLocaleString('pt-BR', opts);
}

const STATUS_COR = {
  PENDENTE:  { bg: '#fff4e6', cor: LARANJA, label: 'Pendente' },
  APROVADO:  { bg: '#dbeafe', cor: AZUL,     label: 'Aprovado' },
  PAGO:      { bg: '#e6f4ea', cor: VERDE,    label: 'Pago' },
  RECUSADO:  { bg: '#fdecea', cor: VERMELHO, label: 'Recusado' },
  CANCELADO: { bg: '#eee',    cor: CINZA,    label: 'Cancelado' },
};

const TIPO_EMAIL_LABEL = {
  nova_solicitacao_saque: 'Nova solicitação (aos aprovadores)',
  saque_aprovado: 'Aprovado (ao arquiteto)',
  saque_recusado: 'Recusado (ao arquiteto)',
};

function Pill({ status }) {
  const s = STATUS_COR[status] || { bg: '#eee', cor: '#666', label: status || '—' };
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 12,
      fontSize: '.74rem', fontWeight: 600, background: s.bg, color: s.cor,
      textTransform: 'uppercase', letterSpacing: '.03em',
    }}>{s.label}</span>
  );
}

function StatusEmail({ status }) {
  const map = {
    ok:               { bg: '#e6f4ea', cor: VERDE,    icone: 'bi-check-circle', txt: 'Enviado' },
    falhou:           { bg: '#fdecea', cor: VERMELHO, icone: 'bi-x-circle',     txt: 'Falhou' },
    sem_destinatario: { bg: '#fff4e6', cor: LARANJA,  icone: 'bi-exclamation-triangle', txt: 'Sem destinatário' },
  };
  const s = map[status] || { bg: '#eee', cor: '#666', icone: 'bi-question-circle', txt: status };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 10, fontSize: '.74rem', fontWeight: 600,
      background: s.bg, color: s.cor,
    }}>
      <i className={`bi ${s.icone}`} /> {s.txt}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Modal de detalhe
// ---------------------------------------------------------------------------
function ModalDetalhe({ id, onClose }) {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const safe = useModalSafeClose(onClose);

  useEffect(() => {
    setCarregando(true);
    api.get(`/auditoria-saques/${id}`)
      .then(r => setDados(r.data))
      .catch(e => setErro(e.response?.data?.error || 'Erro ao carregar detalhe'))
      .finally(() => setCarregando(false));
  }, [id]);

  // ESC fecha o modal (padrão centrao-docs)
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div {...safe.overlayProps} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1050,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 16,
      overflowY: 'auto',
    }}>
      <div {...safe.contentProps} style={{
        background: '#fff', borderRadius: 12, maxWidth: 920, width: '100%',
        marginTop: 30, marginBottom: 30, overflow: 'hidden',
        boxShadow: '0 12px 40px rgba(0,0,0,.25)',
      }}>
        <div style={{
          background: VERDE_ESCURO, color: '#fff', padding: '14px 22px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <i className="bi bi-clipboard-data" style={{ fontSize: '1.25rem' }} />
            <div style={{ fontWeight: 700 }}>
              Auditoria do saque #{id}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'transparent', border: 'none', color: '#fff', fontSize: '1.3rem', cursor: 'pointer',
          }}><i className="bi bi-x-lg" /></button>
        </div>

        <div style={{ padding: 22 }}>
          {carregando && <div className="text-center py-4"><div className="spinner-border" style={{ color: VERDE }} /></div>}
          {erro && <div className="alert alert-danger">{erro}</div>}
          {dados && (
            <>
              {/* Cabeçalho */}
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18,
                background: '#f8faf8', padding: 16, borderRadius: 8, marginBottom: 18,
              }}>
                <div>
                  <div style={{ color: CINZA, fontSize: '.72rem', textTransform: 'uppercase' }}>Arquiteto</div>
                  <div style={{ fontWeight: 600 }}>{dados.arquiteto_nome}</div>
                  <div style={{ color: CINZA, fontSize: '.85rem' }}>{dados.cgc} · {dados.arquiteto_email || '(sem e-mail)'}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: CINZA, fontSize: '.72rem', textTransform: 'uppercase' }}>Valor solicitado</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 700, color: VERDE }}>{brl(dados.valor_solicitado)}</div>
                  <Pill status={dados.status} />
                </div>
                <div>
                  <div style={{ color: CINZA, fontSize: '.72rem', textTransform: 'uppercase' }}>Solicitação criada</div>
                  <div>{fmtData(dados.criado_em)}</div>
                  <div style={{ color: CINZA, fontSize: '.85rem' }}>
                    Chave PIX: {dados.tipo_chave_pix || '—'} · {dados.chave_pix || '—'}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: CINZA, fontSize: '.72rem', textTransform: 'uppercase' }}>Processado por</div>
                  <div style={{ fontWeight: 600 }}>{dados.processado_por_nome || '— (ainda não)'}</div>
                  <div style={{ color: CINZA, fontSize: '.85rem' }}>
                    {dados.processado_em ? fmtData(dados.processado_em) : ''}
                  </div>
                </div>
              </div>

              {/* Observação do arquiteto */}
              {dados.observacao && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ color: CINZA, fontSize: '.72rem', textTransform: 'uppercase', marginBottom: 4 }}>
                    Observação do arquiteto
                  </div>
                  <div style={{ background: '#fff8e6', padding: 10, borderRadius: 6, fontSize: '.92rem' }}>
                    {dados.observacao}
                  </div>
                </div>
              )}

              {/* Resposta do financeiro */}
              {dados.resposta && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ color: CINZA, fontSize: '.72rem', textTransform: 'uppercase', marginBottom: 4 }}>
                    Resposta do financeiro
                  </div>
                  <div style={{
                    background: dados.status === 'RECUSADO' ? '#fdecea' : '#e6f4ea',
                    borderLeft: `4px solid ${dados.status === 'RECUSADO' ? VERMELHO : VERDE}`,
                    padding: 10, borderRadius: 6, fontSize: '.92rem',
                  }}>{dados.resposta}</div>
                </div>
              )}

              {/* Comprovante */}
              {dados.tem_comprovante && (
                <div style={{ marginBottom: 16 }}>
                  <span style={{ color: VERDE, fontSize: '.92rem' }}>
                    <i className="bi bi-paperclip" /> Comprovante anexado: <strong>{dados.comprovante_nome || 'arquivo'}</strong> ({dados.comprovante_mime || '?'})
                  </span>
                  <span style={{ color: CINZA, fontSize: '.78rem', marginLeft: 10 }}>
                    Download disponível no portal financeiro.
                  </span>
                </div>
              )}

              {/* Itens (CREINDs) */}
              <div style={{ marginTop: 16 }}>
                <h6 style={{ color: VERDE_ESCURO, marginBottom: 8 }}>
                  <i className="bi bi-receipt" /> Itens da solicitação ({dados.itens?.length || 0})
                </h6>
                <div style={{ overflowX: 'auto' }}>
                  <table className="table table-sm" style={{ fontSize: '.85rem' }}>
                    <thead style={{ background: '#f0f4f0' }}>
                      <tr>
                        <th>CREIND</th>
                        <th>Emissão</th>
                        <th className="text-end">Valor</th>
                        <th>Ativo</th>
                        <th>Pago no ERP em</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(dados.itens || []).map(i => (
                        <tr key={i.id}>
                          <td>{i.codigo}</td>
                          <td>{fmtData(i.emissao, false)}</td>
                          <td className="text-end">{brl(i.valor)}</td>
                          <td>{i.ativo ? <span style={{ color: VERDE }}>Sim</span> : <span style={{ color: CINZA }}>Não</span>}</td>
                          <td>{i.erp_pagamento ? <span style={{ color: VERDE }}>{fmtData(i.erp_pagamento, false)}</span> : <span style={{ color: CINZA }}>—</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* E-mails enviados */}
              <div style={{ marginTop: 24 }}>
                <h6 style={{ color: VERDE_ESCURO, marginBottom: 8 }}>
                  <i className="bi bi-envelope-paper" /> E-mails enviados ({dados.emails?.length || 0})
                </h6>
                {(!dados.emails || dados.emails.length === 0) && (
                  <div style={{ color: CINZA, fontSize: '.88rem', fontStyle: 'italic' }}>
                    Nenhum e-mail registrado pra essa solicitação.
                  </div>
                )}
                {(dados.emails || []).map(e => (
                  <div key={e.id} style={{
                    border: '1px solid #e3e7e3', borderRadius: 6, padding: 10, marginBottom: 8,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                      <div style={{ fontWeight: 600, fontSize: '.9rem' }}>
                        {TIPO_EMAIL_LABEL[e.tipo] || e.tipo}
                      </div>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <StatusEmail status={e.status} />
                        <span style={{ color: CINZA, fontSize: '.78rem' }}>{fmtData(e.criado_em)}</span>
                      </div>
                    </div>
                    <div style={{ color: CINZA, fontSize: '.82rem', marginTop: 4 }}>
                      Para: <span style={{ color: '#333' }}>{e.destinatarios || '—'}</span>
                    </div>
                    {e.assunto && (
                      <div style={{ color: CINZA, fontSize: '.82rem' }}>
                        Assunto: <span style={{ color: '#333' }}>{e.assunto}</span>
                      </div>
                    )}
                    {e.erro && (
                      <div style={{ color: VERMELHO, fontSize: '.8rem', marginTop: 4 }}>
                        <i className="bi bi-exclamation-circle" /> {e.erro}
                      </div>
                    )}
                    <div style={{ color: '#999', fontSize: '.72rem', marginTop: 2 }}>
                      Origem: {e.origem || '?'}{e.message_id ? ` · ID: ${e.message_id}` : ''}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div style={{
          padding: '12px 22px', borderTop: '1px solid #eee', textAlign: 'right',
          background: '#fafafa',
        }}>
          <button onClick={onClose} className="btn btn-secondary btn-sm">Fechar</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Página principal
// ---------------------------------------------------------------------------
export default function AuditoriaSaques() {
  const [lista, setLista] = useState([]);
  const [total, setTotal] = useState(0);
  const [resumo, setResumo] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroArquiteto, setFiltroArquiteto] = useState('');
  const [filtroDe, setFiltroDe] = useState('');
  const [filtroAte, setFiltroAte] = useState('');
  const [offset, setOffset] = useState(0);
  const LIMIT = 50;

  const [modalId, setModalId] = useState(null);

  const carregar = useCallback(() => {
    setCarregando(true);
    setErro('');
    const params = { limit: LIMIT, offset };
    if (filtroStatus) params.status = filtroStatus;
    if (filtroArquiteto) params.arquiteto = filtroArquiteto;
    if (filtroDe) params.de = filtroDe;
    if (filtroAte) params.ate = filtroAte;
    api.get('/auditoria-saques', { params })
      .then(r => { setLista(r.data.rows || []); setTotal(r.data.total || 0); })
      .catch(e => setErro(e.response?.data?.error || 'Erro ao carregar'))
      .finally(() => setCarregando(false));
  }, [filtroStatus, filtroArquiteto, filtroDe, filtroAte, offset]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    api.get('/auditoria-saques/resumo')
      .then(r => setResumo(r.data))
      .catch(() => {});
  }, []);

  function limparFiltros() {
    setFiltroStatus(''); setFiltroArquiteto(''); setFiltroDe(''); setFiltroAte(''); setOffset(0);
  }

  return (
    <div style={{ padding: '20px 22px' }}>
      <div style={{ marginBottom: 18 }}>
        <h4 style={{ color: VERDE_ESCURO, marginBottom: 4 }}>
          <i className="bi bi-clipboard-data" style={{ color: LARANJA }} /> Auditoria de Saques
        </h4>
        <p style={{ color: CINZA, fontSize: '.9rem', margin: 0 }}>
          Histórico completo de solicitações dos arquitetos. O pagamento é feito no{' '}
          <a href="https://financeiro.centraomateriais.com/arquitetos-saques" target="_blank" rel="noreferrer" style={{ color: VERDE }}>
            Portal Financeiro
          </a> — esta tela é só pra acompanhamento.
        </p>
      </div>

      {/* Resumo (últimos 30 dias) */}
      {resumo && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10,
          marginBottom: 18,
        }}>
          <Card titulo="Pagos (30d)"      valor={resumo.solicitacoes.pagos || 0}      sub={brl(resumo.solicitacoes.total_pago || 0)} cor={VERDE} />
          <Card titulo="Pendentes"        valor={resumo.solicitacoes.pendentes || 0}  sub={brl(resumo.solicitacoes.total_pendente || 0)} cor={LARANJA} />
          <Card titulo="Recusados (30d)"  valor={resumo.solicitacoes.recusados || 0}  cor={VERMELHO} />
          <Card titulo="E-mails enviados" valor={resumo.emails.enviados_ok || 0}     sub={`${resumo.emails.falhou || 0} falharam`} cor={AZUL} />
        </div>
      )}

      {/* Filtros */}
      <div style={{
        background: '#fff', border: '1px solid #e3e7e3', borderRadius: 8, padding: 14, marginBottom: 14,
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10, alignItems: 'end' }}>
          <div>
            <label style={{ fontSize: '.78rem', color: CINZA }}>Status</label>
            <select className="form-select form-select-sm" value={filtroStatus}
              onChange={e => { setOffset(0); setFiltroStatus(e.target.value); }}>
              <option value="">Todos</option>
              <option value="PENDENTE">Pendente</option>
              <option value="PAGO">Pago</option>
              <option value="RECUSADO">Recusado</option>
              <option value="CANCELADO">Cancelado</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: '.78rem', color: CINZA }}>Arquiteto / CPF / e-mail</label>
            <input className="form-control form-control-sm" placeholder="Buscar..."
              value={filtroArquiteto}
              onChange={e => { setOffset(0); setFiltroArquiteto(e.target.value); }} />
          </div>
          <div>
            <label style={{ fontSize: '.78rem', color: CINZA }}>De</label>
            <input type="date" className="form-control form-control-sm"
              value={filtroDe} onChange={e => { setOffset(0); setFiltroDe(e.target.value); }} />
          </div>
          <div>
            <label style={{ fontSize: '.78rem', color: CINZA }}>Até</label>
            <input type="date" className="form-control form-control-sm"
              value={filtroAte} onChange={e => { setOffset(0); setFiltroAte(e.target.value); }} />
          </div>
          <div>
            <button className="btn btn-outline-secondary btn-sm w-100" onClick={limparFiltros}>
              <i className="bi bi-x-circle" /> Limpar
            </button>
          </div>
        </div>
      </div>

      {erro && <div className="alert alert-danger">{erro}</div>}

      {/* Lista */}
      {carregando && (
        <div className="text-center py-5"><div className="spinner-border" style={{ color: VERDE }} /></div>
      )}

      {!carregando && lista.length === 0 && (
        <EmptyState
          icone="bi-inbox"
          titulo="Sem registros"
          descricao="Nenhuma solicitação encontrada com esses filtros."
        />
      )}

      {!carregando && lista.length > 0 && (
        <>
          <div style={{
            background: '#fff', border: '1px solid #e3e7e3', borderRadius: 8, overflow: 'hidden',
          }}>
            <div style={{ overflowX: 'auto' }}>
              <table className="table table-hover mb-0" style={{ fontSize: '.88rem' }}>
                <thead style={{ background: '#f0f4f0', color: VERDE_ESCURO }}>
                  <tr>
                    <th>#</th>
                    <th>Solicitado em</th>
                    <th>Arquiteto</th>
                    <th className="text-end">Valor</th>
                    <th>Status</th>
                    <th>Processado por</th>
                    <th>E-mails</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {lista.map(s => (
                    <tr key={s.id} style={{ cursor: 'pointer' }} onClick={() => setModalId(s.id)}>
                      <td style={{ fontWeight: 600 }}>{s.id}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtData(s.criado_em)}</td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{s.arquiteto_nome}</div>
                        <div style={{ color: CINZA, fontSize: '.78rem' }}>{s.cgc}</div>
                      </td>
                      <td className="text-end" style={{ fontWeight: 600, color: VERDE }}>{brl(s.valor_solicitado)}</td>
                      <td><Pill status={s.status} /></td>
                      <td>
                        {s.processado_por_nome
                          ? (<>
                              <div>{s.processado_por_nome}</div>
                              <div style={{ color: CINZA, fontSize: '.74rem' }}>{fmtData(s.processado_em)}</div>
                            </>)
                          : <span style={{ color: CINZA }}>—</span>}
                      </td>
                      <td>
                        <span style={{ color: AZUL, fontWeight: 600 }}>{s.qtd_emails}</span>
                        {s.qtd_emails_falhou > 0 && (
                          <span style={{ color: VERMELHO, marginLeft: 6, fontSize: '.78rem' }}>
                            <i className="bi bi-exclamation-triangle" /> {s.qtd_emails_falhou}
                          </span>
                        )}
                      </td>
                      <td className="text-end">
                        <button className="btn btn-sm btn-outline-success" onClick={ev => { ev.stopPropagation(); setModalId(s.id); }}>
                          <i className="bi bi-eye" /> Detalhes
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Paginação */}
          <div style={{
            marginTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            color: CINZA, fontSize: '.85rem',
          }}>
            <div>Mostrando {offset + 1}–{Math.min(offset + LIMIT, total)} de {total}</div>
            <div className="btn-group btn-group-sm">
              <button className="btn btn-outline-secondary" disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - LIMIT))}>
                <i className="bi bi-chevron-left" /> Anterior
              </button>
              <button className="btn btn-outline-secondary" disabled={offset + LIMIT >= total}
                onClick={() => setOffset(offset + LIMIT)}>
                Próxima <i className="bi bi-chevron-right" />
              </button>
            </div>
          </div>
        </>
      )}

      {modalId && <ModalDetalhe id={modalId} onClose={() => setModalId(null)} />}
    </div>
  );
}

function Card({ titulo, valor, sub, cor }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid #e3e7e3', borderRadius: 8,
      padding: 12, borderLeft: `4px solid ${cor}`,
    }}>
      <div style={{ color: CINZA, fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: '.04em' }}>{titulo}</div>
      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: cor }}>{valor}</div>
      {sub && <div style={{ color: CINZA, fontSize: '.78rem' }}>{sub}</div>}
    </div>
  );
}
